import React, { useEffect, useRef, useState } from "react";

/*
 * Homepage motion primitives - dependency-free (no framer-motion), built on the
 * IntersectionObserver + rAF so the marketing bundle stays lean. Everything here
 * is gated behind `prefers-reduced-motion`: when the user prefers reduced motion
 * (or the APIs are unavailable, e.g. SSR/old browsers) content renders in its
 * final state immediately and no animation runs.
 *
 * Animations use transform/opacity only (compositor-friendly) per the UX
 * performance guidelines - never animate width/height/top.
 */

// True when the OS-level "reduce motion" preference is set. Reactive to changes.
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

// "Has this element been scrolled into view yet" - fires once, then unobserves.
// Returns [ref, visible]. Reports visible:true immediately under reduced motion
// (or when IntersectionObserver is unavailable) so nothing stays hidden.
export function useInView<T extends HTMLElement = HTMLDivElement>(opts?: {
  threshold?: number;
  rootMargin?: string;
}): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const reduced = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);
  const threshold = opts?.threshold ?? 0.15;
  const rootMargin = opts?.rootMargin ?? "0px 0px -8% 0px";
  useEffect(() => {
    if (reduced) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduced, threshold, rootMargin]);
  return [ref, visible];
}

// Scroll-reveal wrapper: fades + lifts its children into place the first time
// they enter the viewport. `delay` (ms) staggers siblings; `y` is the lift
// distance. Renders as a plain element under reduced motion (no transition).
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  y?: number;
  durationMs?: number;
  style?: React.CSSProperties;
  className?: string;
}> = ({ children, delay = 0, y = 24, durationMs = 620, style, className }) => {
  const [ref, visible] = useInView<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  if (reduced) {
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : `translate3d(0, ${y}px, 0)`,
        transition: `opacity ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        transitionDelay: `${delay}ms`,
        willChange: visible ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

// Count-up: animates a leading integer from 0 → target when scrolled into view,
// preserving any non-numeric prefix/suffix ("20+", "100%"). Values whose
// suffix/prefix still contain digits ("24/7") render verbatim - counting them up
// would look wrong - as do values under reduced motion.
export const CountUp: React.FC<{
  value: string;
  durationMs?: number;
  style?: React.CSSProperties;
  className?: string;
}> = ({ value, durationMs = 1500, style, className }) => {
  const [ref, visible] = useInView<HTMLSpanElement>();
  const reduced = usePrefersReducedMotion();

  // prefix (non-digit) · numeric core (digits + . , separators) · suffix
  const m = value.match(/^(\D*)(\d[\d.,]*)(.*)$/s);
  const animatable = !!m && !reduced && !/\d/.test(m[3]);

  const prefix = m ? m[1] : "";
  const numStr = m ? m[2] : value;
  const suffix = m ? m[3] : "";
  const sep = numStr.includes(".") ? "." : numStr.includes(",") ? "," : "";
  const target = m ? parseInt(numStr.replace(/[.,]/g, ""), 10) : 0;

  const [n, setN] = useState(animatable ? 0 : target);

  useEffect(() => {
    if (!animatable || !visible) return;
    let raf = 0;
    let start = 0;
    const step = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [animatable, visible, target, durationMs]);

  const fmt = (x: number) =>
    sep ? x.toLocaleString(sep === "." ? "de-DE" : "en-US") : String(x);

  return (
    <span ref={ref} className={className} style={style}>
      {animatable ? `${prefix}${fmt(n)}${suffix}` : value}
    </span>
  );
};
